/* eslint-disable react-native/no-inline-styles */
import React, { useEffect, Suspense, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Pressable, Image } from 'react-native';
import Placeholder from '@web/plugins/placeholder';
import {
  createStackNavigator,
  CardStyleInterpolators,
} from '@react-navigation/stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  headerTitleStyle,
  headerLeftContainerStyle,
  headerBackTitleStyle,
  headerTitleContainerStyle,
} from '~assets/styles/headerStyles';

const key = '@storage_header';
const Stack = createStackNavigator();

// global
import { storeAsyncData, getAsyncData } from '~global/helpers';
import { isWeb, isMobileWeb } from '~global/screens';

// state management
import * as authActions from '~store/user/actions/auth';

// lazy components
// screens
const ProfileScreen = React.lazy(() => import('@web/screens/ProfileScreen'));
const MyOrdersScreen = React.lazy(() => import('@web/screens/MyOrdersScreen'));
const WishlistScreen = React.lazy(() => import('@web/screens/WishlistScreen'));
const LoginControlScreen = React.lazy(() =>
  import('@web/screens/LoginControlScreen'),
);
const SignupScreen = React.lazy(() => import('@web/screens/SignupScreen'));

const MyAddressScreen = React.lazy(() =>
  import('@web/screens/MyAddressScreen'),
);

const FormAddressScreen = React.lazy(() =>
  import('@web/screens/FormAddressScreen'),
);

const FormEditAddressScreen = React.lazy(() =>
  import('@web/screens/FormEditAddressScreen'),
);

const EditProfileScreen = React.lazy(() =>
  import('@web/screens/EditProfileScreen'),
);
const OtpVerificationControlScreen = React.lazy(() =>
  import('@web/screens/OtpVerificationControlScreen'),
);
const SettingsScreen = React.lazy(() => import('@web/screens/SettingsScreen'));
const ChangePasswordScreen = React.lazy(() =>
  import('@web/screens/ChangePasswordScreen'),
);
const ForgotPasswordScreen = React.lazy(() =>
  import('@web/screens/ForgotPasswordScreen'),
);
const OrderDetailScreen = React.lazy(() =>
  import('@web/screens/OrderDetailScreen'),
);
const HowToPayScreen = React.lazy(() => import('@web/screens/HowToPayScreen'));
const PaymentConfirmationScreen = React.lazy(() =>
  import('@web/screens/PaymentConfirmationScreen'),
);
const PaymentConfirmationSuccessScreen = React.lazy(() =>
  import('@web/screens/PaymentConfirmationSuccessScreen'),
);
const PaymentSuccess = React.lazy(() =>
  import('@web/screens/payment/PaymentSuccess'),
);
const PaymentWaiting = React.lazy(() =>
  import('@web/screens/payment/PaymentWaiting'),
);
const NewsletterScreen = React.lazy(() =>
  import('@web/screens/NewsletterScreen'),
);
const HelpScreen = React.lazy(() => import('@web/screens/help/HelpScreen'));
const TransactionProblemsScreen = React.lazy(() =>
  import('@web/screens/help/TransactionProblemsScreen'),
);
const TransactionDetailScreen = React.lazy(() =>
  import('@web/screens/help/TransactionDetailScreen'),
);
const ContactUsScreen = React.lazy(() =>
  import('@web/screens/help/ContactUsScreen'),
);
const LanguageScreen = React.lazy(() => import('@web/screens/LanguageScreen'));
const InterestScreen = React.lazy(() => import('@web/screens/InterestScreen'));
const ShoppingBagScreen = React.lazy(() =>
  import('@web/screens/ShoppingBagScreen'),
);
const ShippingScreen = React.lazy(() => import('@web/screens/ShippingScreen'));
const EditImageScreen = React.lazy(() =>
  import('@web/screens/EditImageScreen'),
);
const PaymentControl = React.lazy(() =>
  import('@web/screens/payment/PaymentControl'),
);
const DetailPayment = React.lazy(() =>
  import('@web/screens/payment/DetailPayment'),
);
const EmailVerification = React.lazy(() =>
  import('@web/screens/EmailVerification'),
);
const ModalOrderRetry = React.lazy(() =>
  import('@web-components/modals/ModalOrderRetry'),
);
const ModalOrdersFilter = React.lazy(() =>
  import('@web-components/modals/ModalOrdersFilter'),
);
const ModalUserQR = React.lazy(() =>
  import('@web-components/modals/ModalUserQR'),
);
const ModalTrackOrder = React.lazy(() =>
  import('@web-components/modals/ModalTrackOrder'),
);
const ModalPackageReceived = React.lazy(() =>
  import('@web-components/modals/ModalPackageReceived'),
);
const ModalPackageReceivedSuccess = React.lazy(() =>
  import('@web-components/modals/ModalPackageReceivedSuccess'),
);
const RightHeader = React.lazy(() =>
  import('@web-components/headers/RightHeader'),
);
const LoginHeader = React.lazy(() =>
  import('@web-components/headers/LoginHeader'),
);
const SignupHeader = React.lazy(() =>
  import('@web-components/headers/SignupHeader'),
);

// navigators
const CategoriesNavigator = React.lazy(() =>
  import('@web/navigation/CategoriesNavigator'),
);

//Review
const MyReviewScreen = React.lazy(() => import('@web/screens/MyReviewScreen'));
const CreateReviewScreen = React.lazy(() =>
  import('@web/screens/CreateReviewScreen'),
);
const EditReviewScreen = React.lazy(() =>
  import('@web/screens/EditReviewScreen'),
);
const AddPhotoReviewScreen = React.lazy(() =>
  import('@web/screens/AddPhotoReviewScreen'),
);

const ExpectingMotherOnboardingScreen = React.lazy(() =>
  import('@web/screens/ExpectingMotherOnboardingScreen'),
);
const MotherWithKidsOnboardingScreen = React.lazy(() =>
  import('@web/screens/MotherWithKidsOnboardingScreen'),
);

const ProductDetailScreen = React.lazy(() =>
  import('@web/screens/ProductDetailScreen'),
);
const AllReviewProductDetailScreen = React.lazy(() =>
  import('@web/screens/AllReviewProductDetailScreen'),
);
const HomeHeader = React.lazy(() =>
  import('@web-components/headers/HomeHeader'),
);
const StoreScreen = React.lazy(() => import('@web/screens/StoreScreen'));

const FormAddressHeader = React.lazy(() =>
  import('@web-components/headers/FormAddressHeader'),
);
const Banner = React.lazy(() => import('@web-components/store/Banner'));
const ParentVerificationEmailScreen = React.lazy(() =>
  import('@web/screens/ParentVerificationEmailScreen'),
);

const ProfileNavigator = () => {
  const insets = useSafeAreaInsets();
  const dispatch = useDispatch();
  const [showHeaderLogin, setShowHeaderLogin] = useState(false);
  const paymentScreen = useSelector((state) => state.payment.paymentScreen);

  useEffect(() => {
    const tryLogin = async () => {
      dispatch(authActions.getUserDetail());
    };
    tryLogin();
  }, [dispatch]);

  useEffect(() => {
    getAsyncData(key).then((value) => {
      if (value) {
        setShowHeaderLogin(false);
      } else {
        setShowHeaderLogin(true);
      }
    });
  }, [showHeaderLogin]);

  const backScreenPaymentControl = (e) => {
    if (paymentScreen === 'detail_payment') {
      dispatch({ type: 'PAYMENT/SET_PAYMENT_SCREEN', data: 'list_payments' });
      e.preventDefault();
    }
  };

  return (
    <Suspense fallback={<Placeholder />}>
      <Stack.Navigator
        screenOptions={{
          cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
          headerStyle: {
            height: 55 + insets.top,
          },
          cardStyle: {
            flex: 1,
          },
          animationEnabled: false,
          headerStatusBarHeight: insets.top,
          headerTitleStyle,
          headerTitleAlign: 'left',
          headerTitleContainerStyle,
          headerLeftContainerStyle,
          headerBackTitleStyle,
          headerLeft: (props) => (
            <Pressable {...props} style={{ paddingLeft: 16 }}>
              <Image
                style={{ height: 20, width: 20 }}
                source={require('~assets/img/common/arrow-back.png')}
              />
            </Pressable>
          ),
        }}>
        <Stack.Screen
          name="ProfileScreen"
          component={ProfileScreen}
          options={{ title: 'My Profile' }}
        />

        <Stack.Screen
          name="MyOrdersScreen"
          component={MyOrdersScreen}
          options={{ title: 'My Orders' }}
        />

        <Stack.Screen
          name="WishlistScreen"
          component={WishlistScreen}
          options={{ title: 'Wishlist' }}
        />

        <Stack.Screen
          name="MyAddressScreen"
          component={MyAddressScreen}
          options={{
            title: 'Shipping Address',
            // header: props => <AddressHeader {...props} />
          }}
        />

        <Stack.Screen
          name="FormAddressScreen"
          component={FormAddressScreen}
          options={({ route: { params } }) => ({ title: params.title })}
        />

        <Stack.Screen
          name="FormEditAddressScreen"
          component={FormEditAddressScreen}
          options={{
            title: 'Edit Shipping Address',
            ...(isWeb && {
              header: (props) => <FormAddressHeader {...props} />,
            }),
          }}
        />

        <Stack.Screen
          name="OtpVerificationControlScreen"
          component={OtpVerificationControlScreen}
          options={(props) => {
            const newProps = { ...props };
            if (isWeb) {
              newProps.title = 'Verify Your Account';
            } else {
              newProps.title = 'Verification';
            }
            return newProps;
          }}
        />

        <Stack.Screen
          name="EditProfileScreen"
          component={EditProfileScreen}
          options={({ route: { params } }) => ({
            title: params?.title || 'Edit Profile',
          })}
        />

        <Stack.Screen
          name="ExpectingMotherOnboardingScreen"
          component={ExpectingMotherOnboardingScreen}
          options={{ title: 'Megandung' }}
        />

        <Stack.Screen
          name="MotherWithKidsOnboardingScreen"
          component={MotherWithKidsOnboardingScreen}
          options={{ title: 'Ibu dan Anak' }}
        />

        <Stack.Screen
          name="SettingsScreen"
          component={SettingsScreen}
          options={{ title: 'Settings' }}
        />

        <Stack.Screen
          name="ChangePasswordScreen"
          component={ChangePasswordScreen}
          options={{ title: 'Change Password' }}
        />

        <Stack.Screen
          name="EditImageScreen"
          component={EditImageScreen}
          options={{ title: 'Set Photo' }}
        />

        <Stack.Screen
          name="ForgotPasswordScreen"
          component={ForgotPasswordScreen}
          options={{ title: 'Forgot Password' }}
        />

        <Stack.Screen
          name="OrderDetailScreen"
          component={OrderDetailScreen}
          options={{ title: 'Order Detail' }}
        />

        <Stack.Screen
          name="HowToPayScreen"
          component={HowToPayScreen}
          options={{ title: 'How to Pay' }}
        />

        <Stack.Screen
          name="EmailVerification"
          component={EmailVerification}
          options={{ title: 'Verification' }}
        />

        <Stack.Screen
          name="PaymentConfirmationScreen"
          component={PaymentConfirmationScreen}
          options={{ title: 'Payment Confirmation' }}
        />

        <Stack.Screen
          name="PaymentConfirmationSuccessScreen"
          component={PaymentConfirmationSuccessScreen}
          options={{ title: 'Payment Succeed' }}
        />

        <Stack.Screen
          name="InterestScreen"
          component={InterestScreen}
          options={{ title: 'My Interest' }}
        />

        <Stack.Screen
          name="NewsletterScreen"
          component={NewsletterScreen}
          options={(props) => {
            const newProps = { ...props };
            if (isMobileWeb) {
              newProps.title = 'Email Subscription';
            } else {
              newProps.title = 'Newsletter';
            }
            return newProps;
          }}
        />

        <Stack.Screen
          name="HelpScreen"
          component={HelpScreen}
          options={{ title: 'Help Center' }}
        />

        <Stack.Screen
          name="TransactionProblemsScreen"
          component={TransactionProblemsScreen}
          options={{ title: 'Transaction Issues' }}
        />

        <Stack.Screen
          name="TransactionDetailScreen"
          component={TransactionDetailScreen}
          options={{ title: 'Detail Transaction Issues' }}
        />

        <Stack.Screen
          name="ContactUsScreen"
          component={ContactUsScreen}
          options={{ title: 'Contact Us' }}
        />

        <Stack.Screen
          name="CategoriesNavigator"
          component={CategoriesNavigator}
          options={{
            title: 'Categories',
            headerStyle: {
              shadowColor: 'transparent',
              elevation: 0,
              height: 55 + insets.top,
            },
          }}
        />

        <Stack.Screen
          name="LanguageScreen"
          component={LanguageScreen}
          options={{ title: 'Language' }}
        />

        <Stack.Screen
          name="ShoppingBagScreen"
          component={ShoppingBagScreen}
          options={{ title: 'Shopping Bag' }}
        />

        <Stack.Screen
          name="ShippingScreen"
          component={ShippingScreen}
          options={{ title: 'Shipping' }}
        />

        <Stack.Screen
          name="PaymentControl"
          component={PaymentControl}
          options={{
            title: 'Choose Payment',
          }}
          listeners={{
            beforeRemove: (e) => {
              // Prevent default action
              backScreenPaymentControl(e);
            },
          }}
        />

        <Stack.Screen
          name="DetailPayment"
          component={DetailPayment}
          options={{
            title: 'Detail Payment',
          }}
        />

        <Stack.Screen
          name="PaymentSuccess"
          component={PaymentSuccess}
          options={{
            title: 'Payment Succeeded',
            gestureEnabled: false,
          }}
        />

        <Stack.Screen
          name="PaymentWaiting"
          component={PaymentWaiting}
          options={{
            title: 'Waiting For Payment',
            gestureEnabled: false,
          }}
        />

        <Stack.Screen
          name="MyReviewScreen"
          component={MyReviewScreen}
          options={(props) => {
            const newProps = { ...props };
            if (isWeb) {
              newProps.header = (props2) => <HomeHeader {...props2} />;
            }
            newProps.title = 'Review Saya';
            newProps.gestureEnabled = false;
            return newProps;
          }}
        />

        <Stack.Screen
          name="CreateReviewScreen"
          component={CreateReviewScreen}
          options={{
            title: 'Select Product',
            gestureEnabled: false,
          }}
        />

        <Stack.Screen
          name="AddPhotoReviewScreen"
          component={AddPhotoReviewScreen}
          options={{
            title: 'Add Photo',
            gestureEnabled: false,
          }}
        />

        <Stack.Screen
          name="LoginControlScreen"
          component={LoginControlScreen}
          options={({ route: { params } }) => ({
            title: params.title || 'Login',
            ...(isMobileWeb &&
              showHeaderLogin && {
                header: (props) => (
                  <LoginHeader
                    onClose={() => {
                      storeAsyncData(key, true);
                      setShowHeaderLogin(false);
                    }}
                  />
                ),
              }),
          })}
        />
        <Stack.Screen
          name="SignupScreen"
          component={SignupScreen}
          options={{
            title: 'Register',
            ...(isWeb && { header: (props) => <SignupHeader {...props} /> }),
          }}
        />

        <Stack.Screen
          name="EditReviewScreen"
          component={EditReviewScreen}
          options={{
            title: 'Edit Rating',
            gestureEnabled: false,
          }}
        />

        <Stack.Screen
          name="ProductDetailScreen"
          component={ProductDetailScreen}
          options={{
            title: 'Product Detail',
            headerRight: (props) => (
              <RightHeader {...props} showShoppingBag showAdditionalMenu />
            ),
          }}
        />

        <Stack.Screen
          name="StoreScreen"
          component={StoreScreen}
          options={{
            title: 'Our Store',
            ...(isWeb && {
              header: (props) => <HomeHeader {...props} sticky={<Banner />} />,
            }),
          }}
        />

        <Stack.Screen
          name="AllReviewProductDetailScreen"
          component={AllReviewProductDetailScreen}
          options={{
            title: 'All Review',
          }}
        />

        <Stack.Screen
          name="ParentVerificationEmailScreen"
          component={ParentVerificationEmailScreen}
          options={{ title: 'Verifikasi Email Orang Tua / Wali' }}
        />
      </Stack.Navigator>
      <ModalOrderRetry />
      <ModalOrdersFilter />
      <ModalUserQR />
      <ModalTrackOrder />
      <ModalPackageReceived />
      <ModalPackageReceivedSuccess />
    </Suspense>
  );
};

export default ProfileNavigator;
